import {AuthContext} from "./AuthContext.tsx";
import {type ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import type {IUser} from "../models/IUser.ts";
import {accountEndpoints, type IApiResponse, IUserPermissionViewModel} from "../sitemap/apiendponts.ts";
import {toast} from "react-toastify";
import {PermissaoType} from "@assets/models/PermissaoEnum.ts";
import dayjs from "dayjs";

export function AuthProvider({children}: { children: ReactNode }) {
	const [user, setUser] = useState<IUser & { tokenExpires?: number } | undefined>(undefined)
	const [token, setToken] = useState<string | undefined>(undefined)
	const [permissions, setPermissions] = useState<IUserPermissionViewModel[]>([])

	const isAuthenticated = useMemo(() => {
		return user != null
	}, [user]);

	const tokenValidation = useMemo(() => {
		if (user == null || token == null) {
			return {
				expires: dayjs(),
				isExpired: () => true
			}
		}
		return {
			expires: dayjs(user?.tokenExpires),
			isExpired: () => dayjs(user?.tokenExpires).isBefore(dayjs()),
		}
	}, [user, token])

	const fetchPermissions = async (authToken: string) => {
		const ep = accountEndpoints.getPermissions()
		const x = await fetch('/Proxy/' + ep.url, {
			method: ep.method,
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${authToken}`
			}
		});
		const x_1 = await x.json() as IApiResponse<typeof ep['dataType']>;
		setPermissions(x_1.data ?? []);
	}

	const logoutHandler = () => {
		setUser(undefined)
		setToken(undefined)
		setPermissions([])
	}

	const hasPermission = useCallback((permProps: {
		modulo: PermissaoType["modulo"],
		tipo?: PermissaoType["tipo"],
		acao?: string
	} | {
		modulo: PermissaoType["modulo"],
		tipo?: PermissaoType["tipo"],
		acao?: string
	}[]) => {
		const trat = (str?: string) => {
			return str?.replaceAll(' ', '_').toUpperCase()
		}

		if (Array.isArray(permProps)) {
			return permProps.some(pp => permissions.some(p =>
				p.modulo === pp.modulo &&
				(pp?.tipo == null || p.tipo === pp?.tipo || (pp?.tipo === 'visualizar' && p.tipo != null)) &&
				(trat(p.acao) || '') === (trat(pp.acao) || '')
			))
		}

		return permissions.some(p =>
			p.modulo === permProps?.modulo &&
			(permProps?.tipo == null || p.tipo === permProps?.tipo || (permProps?.tipo === 'visualizar' && p.tipo != null)) &&
			(trat(p.acao) || '') === (trat(permProps.acao) || '')
		)
	}, [permissions])

	const loginHandler = async ({username, password}: { username: string, password: string }) => {
		const ep = accountEndpoints.login({username, password})

		await fetch('/Proxy/' + ep.url, {
			method: ep.method,
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify(ep.body)
		}).then(x => x.json()).then((x: IApiResponse<typeof ep.dataType>) => {
			if (x.message != null)
				toast(x.message.message ?? 'Erro ao efetuar login', {
					type: x.message.severity,
					position: 'bottom-center',
					autoClose: 5000
				})

			const minorMessages = x.minorMessages?.filter(m => (m.important || ['error', 'warning'].includes(m.severity))) ?? []
			minorMessages.forEach(m => {
				toast(m.message, {
					type: m.severity,
					position: 'top-right',
				})
			})


			if (x.data != null) {
				setUser({...x.data?.user})
				setToken(x.data?.token)
				void fetchPermissions(x.data.token)
			}
		})
	}

	useEffect(() => {
		const storedUser = localStorage.getItem('user')
		const storedToken = localStorage.getItem('token')
		const storedPermissions = localStorage.getItem('permissions')

		if (storedUser != null && storedToken != null) {
			setUser(JSON.parse(storedUser))
			setToken(storedToken)
			if (storedPermissions != null) {
				setPermissions(JSON.parse(storedPermissions))
			}
			void fetchPermissions(storedToken)
		}
	}, []);

	useEffect(() => {
		if (user == null) {
			localStorage.removeItem('user')
			localStorage.removeItem('token')
			localStorage.removeItem('permissions')
			return
		}

		localStorage.setItem('user', JSON.stringify(user))
		localStorage.setItem('token', token ?? '')
		localStorage.setItem('permissions', JSON.stringify(permissions))
	}, [user, token, permissions]);

	useEffect(() => {
		if (token == null) return
		fetchPermissions(token).then()
	}, [token]);

	useEffect(() => {
		if (user != null && user.tokenExpires == null) {
			toast('Sua sessão expirou', {
				type: 'error', autoClose: 5000
			})
			logoutHandler()
		}
	}, [user]);

	useEffect(() => {
		if (!isAuthenticated || token == null) return

		const intervalId = setInterval(() => {
			if (tokenValidation.isExpired()) {
				toast('Sua sessão expirou', {
					type: 'error', autoClose: 5000
				})
				logoutHandler()
			}
		}, 5000)

		return () => clearInterval(intervalId)
	}, [isAuthenticated, token, tokenValidation]);

	return (<AuthContext.Provider
		value={{
			isAuthenticated,
			user,
			token,
			permissions,
			login: loginHandler,
			logout: logoutHandler,
			refreshPermissions: () => fetchPermissions(localStorage.getItem('token') ?? ''),
			hasPermission
		}}>
		{children}
	</AuthContext.Provider>)
}
